function fwdCartRequestId() {
  try {
    if (window.crypto?.randomUUID) return `cart-${window.crypto.randomUUID()}`;
  } catch (_error) {
    // Fall through to a request-local non-secret identifier.
  }
  return `cart-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function fwdProductContext(productForm) {
  try {
    const node = productForm.querySelector('[data-fwd-product-context]');
    return node ? JSON.parse(node.textContent || '{}') : {};
  } catch (_error) {
    return {};
  }
}

function fwdCartSelectedSize(response) {
  const options = Array.isArray(response?.options_with_values) ? response.options_with_values : [];
  const size = options.find((option) => ['size', '사이즈'].includes(String(option?.name || '').trim().toLowerCase()));
  return size?.value;
}

function fwdTrackCartItemAdded(productForm, response, formData, cartRequestId) {
  const submittedVariantId = String(formData.get('id') || '');
  const responseVariantId = String(response?.variant_id || response?.id || '');
  if (!submittedVariantId || responseVariantId !== submittedVariantId) return false;
  const context = fwdProductContext(productForm);
  const requestedQuantity = Number(formData.get('quantity') || 1);
  try {
    window.fwdAnalytics?.track?.('Cart Item Added', {
      cart_request_id: cartRequestId,
      product_id: response?.product_id || context.productId,
      variant_id: responseVariantId,
      product_category: context.productCategory,
      product_name: response?.product_title || context.productName,
      product_url: context.productUrl,
      variant_title: response?.variant_title || context.variantTitle,
      brand: context.brand || response?.vendor,
      brand_id: context.brandId,
      brand_handle: context.brandHandle,
      brand_url: context.brandUrl,
      size: fwdCartSelectedSize(response) || context.size,
      tagged_sizes: context.taggedSizes,
      recommended_sizes: context.recommendedSizes,
      price: response?.final_price ?? context.price,
      currency: context.currency,
      quantity: Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1,
      cart_quantity: response?.quantity,
      placement: productForm.dataset?.fwdPlacement || (productForm.closest('quick-add-modal') ? 'quick_add' : 'product_detail'),
    });
    return true;
  } catch (_error) {
    return false;
  }
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector('.fwd-product-action-label > span:last-child') ||
          this.submitButton.querySelector('span');
        this.variantUnavailable = this.submitButton.hasAttribute('disabled');
        this.cartAdded = false;
        this.cartStatePending = true;
        this.cartStateRequestId = 0;
        this.onVariantIdChange = this.handleVariantIdChange.bind(this);

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.variantIdInput.addEventListener('change', this.onVariantIdChange);
        if (typeof subscribe === 'function' && PUB_SUB_EVENTS?.cartUpdate) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCartState());
        }
        this.refreshCartState();
      }

      disconnectedCallback() {
        this.variantIdInput.removeEventListener('change', this.onVariantIdChange);
        this.cartUpdateUnsubscriber?.();
      }

      handleVariantIdChange() {
        this.cartAdded = false;
        this.submitButtonText.textContent = window.variantStrings.addToCart;
        this.refreshCartState();
      }

      async refreshCartState() {
        const variantId = String(this.variantIdInput.value || '');
        const requestId = ++this.cartStateRequestId;
        this.cartStatePending = true;
        this.applySubmitButtonState();

        if (!variantId || !window.routes?.cart_url) {
          this.cartStatePending = false;
          this.cartAdded = false;
          this.applySubmitButtonState();
          return;
        }

        try {
          const cartUrl = `${window.routes.cart_url.replace(/\.js$/, '')}.js`;
          const response = await fetch(cartUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error(`Cart state failed: ${response.status}`);
          const cart = await response.json();
          if (requestId !== this.cartStateRequestId || variantId !== String(this.variantIdInput.value || '')) return;
          this.cartAdded = (cart.items || []).some(
            (item) => String(item.variant_id || item.id) === variantId
          );
        } catch (error) {
          if (requestId !== this.cartStateRequestId) return;
          this.cartAdded = false;
        }

        this.cartStatePending = false;
        this.applySubmitButtonState();
      }

      markCurrentVariantAdded() {
        this.cartStateRequestId += 1;
        this.cartStatePending = false;
        this.cartAdded = true;
        this.applySubmitButtonState();
      }

      applySubmitButtonState(text) {
        this.submitButton.dataset.cartAdded = String(this.cartAdded);

        if (this.cartAdded) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButtonText.textContent = window.variantStrings.addedToCart;
          return;
        }

        if (this.variantUnavailable || this.cartStatePending) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (text) this.submitButtonText.textContent = text;
          return;
        }

        this.submitButton.removeAttribute('disabled');
        if (!this.submitButton.classList.contains('loading')) this.submitButton.removeAttribute('aria-disabled');
        this.submitButtonText.textContent = window.variantStrings.addToCart;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const cartRequestId = fwdCartRequestId();
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            fwdTrackCartItemAdded(this, response, formData, cartRequestId);

            if (!this.cart) {
              this.markCurrentVariantAdded();
              window.location = window.routes.cart_url;
              return;
            }

            this.markCurrentVariantAdded();
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.applySubmitButtonState();
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.variantUnavailable = disable;
        this.applySubmitButtonState(text);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
